import { useMemo } from 'react';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { SetOrdering } from '@/lib/local-api/workout/set-ordering';
import type {
  WorkoutSupersetRecord,
  WorkoutExerciseRecord,
  WorkoutSetRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';

export interface WorkoutSetSequenceItem {
  /** The superset this set belongs to */
  superset: WorkoutSupersetRecord;
  /** The exercise this set belongs to */
  exercise: WorkoutExerciseRecord;
  /** The sport activity (exercise) data */
  sportActivity: SportActivityRecord;
  /** The set record */
  set: WorkoutSetRecord;
  /** The set number within the exercise (1-based) */
  setNumber: number;
  /** The overall position in the sequence (0-based) */
  sequenceIndex: number;
  /** Unique key for React rendering */
  key: string;
}

interface UseWorkoutSetSequenceOptions {
  /** Whether to enable the query */
  enabled?: boolean;
}

interface UseWorkoutSetSequenceReturn {
  /** The ordered sequence of sets following round-robin across exercises */
  sequence: WorkoutSetSequenceItem[];
  /** Loading state */
  isLoading: boolean;
  /** Error if any occurred */
  error: Error | null;
}

/**
 * Hook that creates an interleaved sequence of workout sets.
 *
 * For every set that is in an exercise that is in a superset,
 * sets will be shuffled to the sequence so that considering:
 *
 * SS1: EX1: S1,S2,S3
 * SS1: EX2: S1,S2,S3,S4,S5
 * SS2: EX1: S1,S2,S3
 * SS2: EX2: S1,S2,S3
 *
 * The result will be:
 * SS1 EX1 S1, SS1 EX2 S1, SS1 EX1 S2, SS1 EX2 S2, SS1 EX1 S3, SS1 EX2 S3, SS1 EX2 S4, SS1 EX2 S5,
 * SS2 EX1 S1, SS2 EX2 S1, SS2 EX1 S2, SS2 EX2 S2, SS2 EX1 S3, SS2 EX2 S3
 *
 * @param workoutId - The workout ID to generate the sequence for
 * @param options - Additional options
 */
export const useWorkoutSetSequence = (
  workoutId: string,
  options: UseWorkoutSetSequenceOptions = {},
): UseWorkoutSetSequenceReturn => {
  const { enabled = true } = options;

  // Fetch all supersets for the workout
  const {
    data: supersets,
    isLoading: isLoadingSupersets,
    error: supersetsError,
  } = useLivePowerSyncQuery(
    enabled ? db.Workout.queries.findSupersetsWithExercises(workoutId) : null,
    [workoutId],
  );

  // Fetch all exercises for the workout
  const {
    data: allExercises,
    isLoading: isLoadingExercises,
    error: exercisesError,
  } = useLivePowerSyncQuery(
    enabled && supersets?.length
      ? {
          createQuery: (db) =>
            db
              .selectFrom('WorkoutExercise')
              .selectAll()
              .where('workoutId', '=', workoutId)
              .where('deletedAt', 'is', null)
              .orderBy('sortIndex', 'asc'),
          queryKey: ['workout', 'exercises', 'all', workoutId],
        }
      : null,
    [workoutId, supersets],
  );

  // Fetch all sets for the workout
  const {
    data: allSets,
    isLoading: isLoadingSets,
    error: setsError,
  } = useLivePowerSyncQuery(
    enabled && allExercises?.length
      ? {
          createQuery: (db) =>
            db
              .selectFrom('WorkoutSet')
              .selectAll()
              .where('workoutId', '=', workoutId)
              .orderBy('sortIndex', 'asc')
              .orderBy('id', 'asc'),
          queryKey: ['workout', 'sets', 'all', workoutId],
        }
      : null,
    [workoutId, allExercises],
  );

  // Fetch all sport activities for the exercises
  const {
    data: allSportActivities,
    isLoading: isLoadingSportActivities,
    error: sportActivitiesError,
  } = useLivePowerSyncQuery(
    enabled && allExercises?.length
      ? {
          createQuery: (db) => {
            const sportActivityIds = allExercises.map(
              (ex) => ex.sportActivityId,
            );
            return db
              .selectFrom('SportActivity')
              .selectAll()
              .where('id', 'in', sportActivityIds)
              .where('deletedAt', 'is', null);
          },
          queryKey: [
            'sport-activity',
            'by-exercise-ids',
            ...allExercises.map((ex) => ex.id).sort(),
          ],
        }
      : null,
    [allExercises],
  );

  // Generate the interleaved sequence using functional programming
  const sequence = useMemo((): WorkoutSetSequenceItem[] => {
    if (!supersets || !allExercises || !allSets || !allSportActivities) {
      return [];
    }

    // Create a map for quick sport activity lookup
    const sportActivityMap = new Map(
      allSportActivities.map((activity) => [activity.id, activity]),
    );

    return supersets
      .reduce<WorkoutSetSequenceItem[]>((acc, superset) => {
        // Get exercises for this superset, sorted by sortIndex
        const exercises = allExercises
          .filter((ex) => ex.supersetId === superset.id)
          .sort((a, b) => a.sortIndex - b.sortIndex);

        if (exercises.length === 0) {
          return acc;
        }

        // Create a map of exercise ID to sorted sets
        const setsByExercise = new Map(
          exercises.map((exercise) => [
            exercise.id,
            SetOrdering.sort(
              allSets.filter(
                (set) => set.exerciseId === exercise.id,
              ) as unknown as WorkoutSetRecord[],
            ),
          ]),
        );

        // Find the maximum number of sets across all exercises in this superset
        const maxSetCount = Math.max(
          ...exercises.map(
            (exercise) => setsByExercise.get(exercise.id)?.length ?? 0,
          ),
        );

        // Generate round-robin sequence for this superset
        const supersetSequence = Array.from(
          { length: maxSetCount },
          (_, setIndex) =>
            exercises
              .map((exercise) => {
                const exerciseSets = setsByExercise.get(exercise.id) ?? [];
                const set = exerciseSets[setIndex];

                const sportActivity = sportActivityMap.get(
                  exercise.sportActivityId,
                );
                return set && sportActivity
                  ? {
                      superset,
                      exercise,
                      sportActivity,
                      set,
                      setNumber: setIndex + 1,
                      sequenceIndex: 0, // Will be set correctly at the end
                      key: `${superset.id}-${exercise.id}-${set.id}`,
                    }
                  : null;
              })
              .filter((item): item is WorkoutSetSequenceItem => item !== null),
        ).flat();

        return [...acc, ...supersetSequence];
      }, [])
      .map((item, index) => ({ ...item, sequenceIndex: index })); // Assign correct sequential index
  }, [supersets, allExercises, allSets, allSportActivities]);

  const isLoading =
    isLoadingSupersets ||
    isLoadingExercises ||
    isLoadingSets ||
    isLoadingSportActivities;
  const error =
    supersetsError || exercisesError || setsError || sportActivitiesError;

  return {
    sequence,
    isLoading,
    error,
  };
};
